import {Injectable} from '@angular/core';
import * as ProductsActions from './products.actions';
import {map, mergeMap, switchMap, take} from 'rxjs/operators';
import {ProductsPartialState} from './products.reducer';
import {Actions, createEffect} from '@ngrx/effects';
import {
  ActionWithCorrelationAttributes,
  AggregationAction,
  CorrelationAttributes,
  DataPersistence,
  withCorrelationIdOf
} from '@kion/ngrx-helper';
import {Product, ProductSearch} from '@kion/data-models';
import {ProductsQuery} from './products.selectors';
import {combineLatest, of} from 'rxjs';
import {v4 as uuidV4} from 'uuid';
import {ProductsFacade} from './products.facade';
import {ProductConnectors} from "./product.connectors";


@Injectable()
export class ProductsEffects {
  search$ = createEffect(() =>
    this.dataPersistence.fetch(
      ProductsActions.search,
      {
        run: (
          action: ReturnType<typeof ProductsActions.search>
        ) => {
          const searchOptions$ = combineLatest(
            [
              this.dataPersistence.store.select(ProductsQuery.getPagination),
              this.dataPersistence.store.select(ProductsQuery.getQuery),
              this.dataPersistence.store.select(ProductsQuery.getActualSort)
            ]
          );
          return searchOptions$.pipe(
            take(1),
            switchMap(
              ([pagination, query, actualSort]) => {
                return this.backend.search(
                  action.query || query.join(''),
                  pagination.page,
                  pagination.perPage,
                  action.sort || actualSort
                ).pipe(
                  map(
                    (searchResult: ProductSearch) => {
                      return withCorrelationIdOf(
                        action,
                        ProductsActions.searchSuccess({searchResult})
                      )
                    }
                  )
                )
              }
            )
          )
        },
        onError: (
          action: ReturnType<typeof ProductsActions.search>,
          error
        ) => {
          console.error("Error", error);
          return withCorrelationIdOf(
            action,
            ProductsActions.searchError({error})
          )
        }
      }
    )
  );
  textSearch$ = createEffect(() =>
    this.dataPersistence.fetch(
      ProductsActions.textSearch,
      {
        run: (
          action: ReturnType<typeof ProductsActions.textSearch>
        ) => {
          const searchOptions$ = combineLatest(
            [
              this.dataPersistence.store.select(ProductsQuery.getPagination),
              this.dataPersistence.store.select(ProductsQuery.getQuery),
              this.dataPersistence.store.select(ProductsQuery.getActualSort)
            ]
          );
          return searchOptions$.pipe(
            take(1),
            switchMap(
              ([pagination, query, actualSort]) => {
                return this.backend.search(
                  action.query || query.join(''),
                  0,
                  20,
                  action.sort || actualSort
                ).pipe(
                  map(
                    (searchResult: ProductSearch) => {
                      return withCorrelationIdOf(
                        action,
                        ProductsActions.textSearchSuccess({textSearchResult: searchResult})
                      )
                    }
                  )
                )
              }
            )
          )
        },
        onError: (
          action: ReturnType<typeof ProductsActions.textSearch>,
          error
        ) => {
          console.error("Error", error);
          return withCorrelationIdOf(
            action,
            ProductsActions.textSearchError({error})
          )
        }
      }
    )
  );
  textSearchReset = createEffect(() =>
    this.dataPersistence.fetch(
      ProductsActions.textSearchReset,
      {
        run: (
          action: ReturnType<typeof ProductsActions.textSearchReset>
        ) => {
          return withCorrelationIdOf(
            action,
            ProductsActions.textSearchSuccess({
              textSearchResult: {
                products: [],
                facets: [],
                sorts: []
              }
            })
          )
        }
      }
    )
  );
  loadById$ = createEffect(() =>
    this.dataPersistence.fetch(
      ProductsActions.loadById,
      {
        run: (
          action: ReturnType<typeof ProductsActions.loadById>
        ) => {
          return this.backend.getById(action.id).pipe(
            switchMap(
              (response: Product) => {
                const successAction$ = withCorrelationIdOf(
                  action,
                  ProductsActions.loadByIdSuccess({product: response})
                );
                const errorAction$ = withCorrelationIdOf(
                  action,
                  ProductsActions.loadByIdError({error: 'Could not load product'})
                );
                this.aggregateMasterProduct(
                  action,
                  response,
                  successAction$,
                  errorAction$
                );
                return of(successAction$)
              }
            )
          );
        },
        onError: (
          action: ReturnType<typeof ProductsActions.loadById>,
          error
        ) => {
          console.error("ERROR", error);
          return withCorrelationIdOf(
            action,
            ProductsActions.loadByIdError({error: error})
          )
        }
      }
    )
  );
  selectProductVariant$ = createEffect(() =>
    this.dataPersistence.fetch(
      ProductsActions.selectProductVariant,
      {
        run: (
          action: ReturnType<typeof ProductsActions.selectProductVariant>
        ) => {
          return this.backend.getById(action.variantId).pipe(
            map(
              (response: Product) => {
                const product = {...response, isActive: true, masterProduct: action.product.id};
                return withCorrelationIdOf(
                  action,
                  ProductsActions.selectProductVariantSuccess({product: product})
                )
              }
            )
          )
        },
        onError: (
          action: ReturnType<typeof ProductsActions.selectProductVariant>,
          error
        ) => {
          console.error("Error", error);
          return withCorrelationIdOf(
            action,
            ProductsActions.selectProductVariantError({error: error})
          )
        }
      }
    )
  );
  loadByIds$ = createEffect(() =>
    this.dataPersistence.fetch(
      ProductsActions.loadByIds,
      {
        run: (
          action: ReturnType<typeof ProductsActions.loadByIds>
        ) => {
          return this.backend.getByIds(action.ids).pipe(
            map(
              (result: Product[]) => withCorrelationIdOf(
                action,
                ProductsActions.loadByIdsSuccess({products: result})
              )
            )
          );
        },
        onError: (
          action: ReturnType<typeof ProductsActions.loadByIds>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            ProductsActions.loadByIdsError({error: error})
          );
        }
      }
    )
  );
  updateQuery$ = createEffect(() =>
    this.dataPersistence.fetch(
      ProductsActions.updateQuery,
      {
        run: (
          action: ReturnType<typeof ProductsActions.updateQuery>
        ) => {
          const query$ = this.dataPersistence.store.select(ProductsQuery.getQuery);
          return query$.pipe(
            take(1),
            mergeMap(
              (query) => {
                return [
                  withCorrelationIdOf(
                    action,
                    ProductsActions.updateQuerySuccess({query: [action.queryPart], clearAll: action.clearAll})
                  )
                ];
              }
            )
          )
        },
        onError: (
          action: ReturnType<typeof ProductsActions.updateQuery>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            ProductsActions.updateQueryError({error: error})
          );
        }
      }
    )
  );
  updateSort$ = createEffect(() =>
    this.dataPersistence.fetch(
      ProductsActions.updateSort,
      {
        run: (
          action: ReturnType<typeof ProductsActions.updateSort>
        ) => {
          const option$ = combineLatest(
            [this.dataPersistence.store.select(ProductsQuery.getQuery)]
          );
          return option$.pipe(
            take(1),
            mergeMap(
              ([query]) => [
                withCorrelationIdOf(
                  action,
                  ProductsActions.updateSortSuccess({sort: action.sort})
                ),
                withCorrelationIdOf(
                  action,
                  ProductsActions.search({query: query.join(''), sort: action.sort, useDefaultPage: true})
                )
              ]
            )
          )
        },
        onError: (
          action: ReturnType<typeof ProductsActions.updateSort>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            ProductsActions.updateSortError({error: error})
          )
        }
      }
    )
  );
  updatePerPage$ = createEffect(() =>
    this.dataPersistence.fetch(
      ProductsActions.updatePerPage,
      {
        run: (
          action: ReturnType<typeof ProductsActions.updatePerPage>
        ) => {
          const option$ = combineLatest(
            [this.dataPersistence.store.select(ProductsQuery.getPagination)]
          );
          return option$.pipe(
            take(1),
            map(
              ([pagination]) => {
                return withCorrelationIdOf(
                  action,
                  ProductsActions.updatePerPageSuccess({pagination: {...pagination, perPage: action.perPage, page: 1}})
                )
              }
            )
          )
        },
        onError: (
          action: ReturnType<typeof ProductsActions.updatePerPage>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            ProductsActions.updatePerPageError({error: error})
          );
        }
      }
    )
  );
  updatePage$ = createEffect(() =>
    this.dataPersistence.fetch(
      ProductsActions.updatePage,
      {
        run: (
          action: ReturnType<typeof ProductsActions.updatePage>
        ) => {
          const option$ = combineLatest(
            [this.dataPersistence.store.select(ProductsQuery.getPagination)]
          );
          return option$.pipe(
            take(1),
            map(
              ([pagination]) => {
                return withCorrelationIdOf(
                  action,
                  ProductsActions.updatePageSuccess({pagination: {...pagination, page: action.page}})
                )
              }
            )
          )
        },
        onError: (
          action: ReturnType<typeof ProductsActions.updatePage>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            ProductsActions.updatePageError({error: error})
          );
        }
      }
    )
  );
  loadProductReference$ = createEffect(() =>
    this.dataPersistence.fetch(
      ProductsActions.loadProductReference,
      {
        run: (
          action: ReturnType<typeof ProductsActions.loadProductReference>
        ) => {
          return this.backend.getReferencesForProduct(action.productId, action.references).pipe(
            map(
              (response: Product[]) => withCorrelationIdOf(
                action,
                ProductsActions.loadProductReferenceSuccess({products: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof ProductsActions.loadProductReference>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            ProductsActions.loadProductReferenceError({error: error})
          )
        }
      }
    )
  );
  setActiveProduct$ = createEffect(() =>
    this.dataPersistence.fetch(
      ProductsActions.setActiveProduct,
      {
        run: (
          action: ReturnType<typeof ProductsActions.setActiveProduct>
        ) => {
          return withCorrelationIdOf(
            action,
            ProductsActions.setActiveProductSuccess({productId: action.productId})
          )
        },
        onError: (
          action: ReturnType<typeof ProductsActions.setActiveProduct>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            ProductsActions.setActiveProductError({error: error})
          );
        }
      }
    )
  );
  setNoResult$ = createEffect(() =>
    this.dataPersistence.fetch(
      ProductsActions.setNoResult,
      {
        run: (
          action: ReturnType<typeof ProductsActions.setNoResult>
        ) => withCorrelationIdOf(
          action,
          ProductsActions.setNoResultSuccess({toggle: action.toggle})
        ),
        onError: (
          action: ReturnType<typeof ProductsActions.setNoResult>
        ) => withCorrelationIdOf(
          action,
          ProductsActions.setNoResultError()
        )
      },
    )
  );

  constructor(
    private actions$: Actions,
    private dataPersistence: DataPersistence<ProductsPartialState>,
    private backend: ProductConnectors,
    private productsFacade: ProductsFacade
  ) {
  }

  private aggregateMasterProduct(
    action: ActionWithCorrelationAttributes,
    product: Product,
    successAction$,
    errorAction$
  ): AggregationAction {
    const correlationParams: Partial<CorrelationAttributes> = {
      __correlationId: action.__correlationId ? action.__correlationId : uuidV4(),
    };

    const waitForAction = [
      successAction$
    ];
    if (product.variantOptions) {
      const codes = product.variantOptions.map(option => option.code);
      waitForAction.push(
        this.productsFacade.loadByIds(codes)
      )
    }

    return new AggregationAction(
      {
        successAction: successAction$,
        errorAction: errorAction$,
        waitFor: waitForAction,
        failOn: [
          errorAction$
        ]
      },
      correlationParams.__correlationId
    );
  }
}



